import { closestCenter, DndContext } from '@dnd-kit/core';
import {
  restrictToHorizontalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import {
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import styles from './ApplicationsTable.module.css';
import { Cell } from './Cells/Cell';
import { DraggableTableHeader } from './Cells/DraggableTableHeader/DraggableTableHeader';
import { TableStatus } from './TableStatus/TableStatus';
import { useApplicationsTable } from './useApplicationsTable.hook';
import { useVirtual } from './useVirtual.hook';

export const ApplicationsTable = () => {
  const {
    rows,
    columnOrder,
    headerGroups,
    sensors,
    table,
    handleDragEnd,
    handleRowClick,
  } = useApplicationsTable();
  const { virtualRows, paddingTop, paddingBottom, parentRef } =
    useVirtual(rows);

  const noResults = rows.length === 0;

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis, restrictToParentElement]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <div className={styles.tableContainer}>
        <table className={`${styles.table} ${styles.headerWrapper}`}>
          <colgroup>
            {table.getHeaderGroups()[0].headers.map((header) => (
              <col key={header.id} style={{ width: header.getSize() }} />
            ))}
          </colgroup>
          <thead className={styles.header}>
            {headerGroups.map((headerGroup) => (
              <tr key={headerGroup.id}>
                <SortableContext
                  items={columnOrder}
                  strategy={horizontalListSortingStrategy}
                >
                  {headerGroup.headers.map((header) => (
                    <DraggableTableHeader key={header.id} header={header} />
                  ))}
                </SortableContext>
              </tr>
            ))}
          </thead>
        </table>
        {noResults ? (
          <TableStatus />
        ) : (
          <div ref={parentRef} className={styles.scrollableRows}>
            <table className={styles.table}>
              <colgroup>
                {table.getHeaderGroups()[0].headers.map((header) => (
                  <col key={header.id} style={{ width: header.getSize() }} />
                ))}
              </colgroup>
              <tbody>
                {/* Push the first rendered row down to its correct position in the scroll. */}
                {paddingTop > 0 && (
                  <tr>
                    <td style={{ height: paddingTop }} />
                  </tr>
                )}
                {virtualRows.map((virtualRow) => {
                  const row = rows[virtualRow.index];

                  return (
                    <tr
                      key={row.id}
                      className={styles.row}
                      style={{ height: `${virtualRow.size}px` }}
                      onClick={() => {
                        handleRowClick(row.original);
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <Cell key={cell.id} cell={cell} />
                      ))}
                    </tr>
                  );
                })}
                {/* Ensure scrollable area extends to the total height of all unrendered rows below the viewport. */}
                {paddingBottom > 0 && (
                  <tr>
                    <td style={{ height: paddingBottom }} />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DndContext>
  );
};
